import { useCallback, useEffect, useRef, useState } from 'react'
import { ProductImageUrls } from '@/utils/productImage'
import { NO_IMAGE_URL } from '@/utils/thumborUtils'
import {
  ProductImageArea,
  reportProductImageIssue
} from '@/core/productImageReporter'

type ProductImageStage = 'primary' | 'fallback' | 'noImage'

type ProductImageTransition = {
  stage: ProductImageStage
  report?: 'recovered' | 'failed'
}

const SLOW_IMAGE_MS = 5000

let isHydrating = true

const slowImageDelay = (mountedDuringHydration: boolean, now: number): number =>
  mountedDuringHydration ? Math.max(0, SLOW_IMAGE_MS - now) : SLOW_IMAGE_MS

const mountedImageEvent = ({
  complete,
  naturalWidth,
  currentSrc
}: Pick<HTMLImageElement, 'complete' | 'naturalWidth' | 'currentSrc'>):
  | 'load'
  | 'error'
  | null => {
  if (!complete) {
    return null
  }
  if (naturalWidth > 0) {
    return 'load'
  }
  return currentSrc ? 'error' : null
}

const nextProductImageStage = (
  stage: ProductImageStage,
  event: 'load' | 'error'
): ProductImageTransition => {
  if (stage === 'primary') {
    return { stage: event === 'error' ? 'fallback' : 'primary' }
  }
  if (stage === 'fallback') {
    return event === 'error'
      ? { stage: 'noImage', report: 'failed' }
      : { stage: 'fallback', report: 'recovered' }
  }
  return { stage: 'noImage' }
}

export const useProductImage = (
  urls: ProductImageUrls,
  { area, measureSlow }: { area: ProductImageArea; measureSlow: boolean }
): {
  src: string
  stage: ProductImageStage
  onLoad: () => void
  onError: () => void
  ref: (img: HTMLImageElement | null) => void
} => {
  const [state, setState] = useState<{
    primary: string
    stage: ProductImageStage
  }>({ primary: urls.primary, stage: 'primary' })
  const stage = state.primary === urls.primary ? state.stage : 'primary'
  const settled = useRef<string>(null)
  const firstTimerDuringHydration = useRef(isHydrating)
  const checkedImages = useRef(new WeakSet<HTMLImageElement>())

  const report = useCallback(
    (outcome: 'slow' | 'recovered' | 'failed') => {
      if (urls.primary === NO_IMAGE_URL) {
        return
      }
      reportProductImageIssue({
        outcome,
        area,
        primaryUrl: urls.primary,
        fallbackUrl: urls.fallback
      })
    },
    [urls.primary, urls.fallback, area]
  )

  const apply = (transition: ProductImageTransition) => {
    settled.current = urls.primary
    if (transition.stage !== stage) {
      setState({ primary: urls.primary, stage: transition.stage })
    }
    if (transition.report) {
      report(transition.report)
    }
  }

  const onLoad = () => apply(nextProductImageStage(stage, 'load'))

  const onError = () =>
    apply(
      stage === 'primary' && urls.primary === urls.fallback
        ? { stage: 'noImage', report: 'failed' }
        : nextProductImageStage(stage, 'error')
    )

  const ref = (img: HTMLImageElement | null) => {
    if (!img || checkedImages.current.has(img)) {
      return
    }
    checkedImages.current.add(img)
    const event = mountedImageEvent(img)
    if (event === 'load') {
      onLoad()
    } else if (event === 'error') {
      onError()
    }
  }

  useEffect(() => {
    if (!measureSlow || stage !== 'primary') {
      return
    }
    if (settled.current === urls.primary) {
      return
    }
    const delay = slowImageDelay(
      firstTimerDuringHydration.current,
      performance.now()
    )
    firstTimerDuringHydration.current = false
    const timer = setTimeout(() => {
      if (settled.current !== urls.primary) {
        report('slow')
      }
    }, delay)
    return () => clearTimeout(timer)
  }, [measureSlow, stage, urls.primary, report])

  useEffect(() => {
    setTimeout(() => {
      isHydrating = false
    })
  }, [])

  return {
    src:
      stage === 'primary'
        ? urls.primary
        : stage === 'fallback'
        ? urls.fallback
        : NO_IMAGE_URL,
    stage,
    onLoad,
    onError,
    ref
  }
}
